import { computed, shallowRef, watch } from "vue";
import { assertNever } from "../lib/assertNever";
import { defineStore } from "pinia";
import {
  displayModeAllowsComments,
  displayModeIsReading,
  displayModeIsWriting,
  type DisplayMode,
} from "../lib/displayMode";
import { readBoolPref, writeBoolPref } from "../lib/localPrefs";
import { useEditorSessionStore } from "./useEditorSessionStore";
import {
  initialDisplayModeModel,
  updateDisplayMode,
  type DisplayModeCmd,
  type DisplayModeMsg,
  type PanelSnapshot,
} from "./useDisplayModeStore.update";

export type { DisplayMode };

function isTryEditorEmbed(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.dataset.zwEmbed === "1"
  );
}

/**
 * Stored writing-surface mode plus enter/exit side effects (panel hide-set,
 * typewriter pref). Share sessions call `lockMode` so Reading cannot be exited.
 *
 * TEA: pure Model/Msg in `useDisplayModeStore.update.ts`. Session panel
 * Cmds run synchronously so callers/tests see them immediately.
 */
export const useDisplayModeStore = defineStore("displayMode", () => {
  const session = useEditorSessionStore();
  const model = shallowRef(
    initialDisplayModeModel(readBoolPref("focusTypewriter")),
  );

  function runCmd(cmd: DisplayModeCmd): void {
    switch (cmd.kind) {
      case "restorePanels":
        session.outlineOpen = cmd.snapshot.outlineOpen;
        session.commentsOpen = cmd.snapshot.commentsOpen;
        session.historyOpen = cmd.snapshot.historyOpen;
        session.goalsOpen = cmd.snapshot.goalsOpen;
        return;
      case "hideFocusPanels":
        session.outlineOpen = false;
        session.commentsOpen = false;
        session.historyOpen = false;
        session.goalsOpen = false;
        return;
      case "hideReadingSidePanels":
        if (session.historyOpen) session.historyOpen = false;
        if (session.goalsOpen) session.goalsOpen = false;
        return;
      case "persistTypewriterPref":
        writeBoolPref("focusTypewriter", cmd.value);
        return;
      default:
        assertNever(cmd);
    }
  }

  function dispatch(msg: DisplayModeMsg): void {
    const [next, cmds] = updateDisplayMode(model.value, msg);
    model.value = next;
    for (const cmd of cmds) {
      runCmd(cmd);
    }
  }

  const displayMode = computed(() => model.value.displayMode);
  const modeLocked = computed(() => model.value.modeLocked);
  const typewriterPref = computed(() => model.value.typewriterPref);

  const typewriterActive = computed(
    () => model.value.displayMode === "focus" && model.value.typewriterPref,
  );

  const isWriting = computed(() =>
    displayModeIsWriting(model.value.displayMode),
  );
  const isFocus = computed(() => model.value.displayMode === "focus");
  const isReading = computed(() =>
    displayModeIsReading(model.value.displayMode),
  );
  const allowsComments = computed(() =>
    displayModeAllowsComments(model.value.displayMode),
  );

  /** Manuscript Writing. Not Notes/DB/map/embed/Storyboard. */
  const modeEligible = computed(() => {
    if (isTryEditorEmbed()) return false;
    if (session.activeMap) return false;
    if (session.editingSidecar) return false;
    if (session.storyboardView) return false;
    return true;
  });

  const focusModeEligible = computed(
    () => modeEligible.value && model.value.modeLocked === false,
  );
  const readingModeEligible = computed(
    () => modeEligible.value && model.value.modeLocked === false,
  );

  function currentPanelSnapshot(): PanelSnapshot {
    return {
      outlineOpen: session.outlineOpen,
      commentsOpen: session.commentsOpen,
      historyOpen: session.historyOpen,
      goalsOpen: session.goalsOpen,
    };
  }

  function lockMode(locked: boolean): void {
    dispatch({ type: "LockMode", locked });
  }

  /**
   * Set the surface mode. Share boot uses this to force Reading without
   * eligibility checks; owner toggles go through enter/exit helpers.
   */
  function setDisplayMode(mode: DisplayMode): void {
    dispatch({ type: "SetDisplayMode", mode });
  }

  function enterFocusMode(): void {
    if (
      model.value.displayMode === "focus" ||
      focusModeEligible.value === false
    ) {
      return;
    }
    dispatch({ type: "EnterFocus", snapshot: currentPanelSnapshot() });
  }

  function exitFocusMode(): void {
    dispatch({ type: "ExitFocus" });
  }

  function toggleFocusMode(): void {
    if (model.value.displayMode === "focus") exitFocusMode();
    else enterFocusMode();
  }

  /** Owner Reading: prose read-only, comments on. */
  function enterReadingMode(): void {
    if (isReading.value || readingModeEligible.value === false) return;
    dispatch({ type: "EnterReading" });
  }

  function exitReadingMode(): void {
    dispatch({ type: "ExitReading" });
  }

  function toggleReadingMode(): void {
    if (isReading.value) exitReadingMode();
    else enterReadingMode();
  }

  function toggleTypewriterPref(): void {
    dispatch({ type: "ToggleTypewriterPref" });
  }

  // Leaving Writing (Notes / Database / map) drops Focus / Reading rather than
  // trapping the user in a mode that no longer applies. Share stays locked.
  watch(modeEligible, (eligible) => {
    if (eligible || model.value.modeLocked) return;
    dispatch({ type: "EligibilityLost" });
  });

  return {
    displayMode,
    modeLocked,
    isWriting,
    isFocus,
    isReading,
    allowsComments,
    modeEligible,
    focusModeEligible,
    readingModeEligible,
    lockMode,
    setDisplayMode,
    toggleFocusMode,
    enterFocusMode,
    exitFocusMode,
    toggleReadingMode,
    enterReadingMode,
    exitReadingMode,
    typewriterPref,
    toggleTypewriterPref,
    typewriterActive,
  };
});
